/**
 * The resolved-with-rows body of RecordSaleDialog: closed/skipped stat
 * cards, the whole-call not-currently-accepted opt-in, and the breakdown
 * table (sale-breakdown-not-accepted-units / "Sale action in the offer
 * workspace"). Split out of record-sale-dialog.tsx to keep that component a
 * thin composition and this file's own conditionals off its complexity
 * budget.
 */
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { Label } from '@ooh/ui/components/label';
import { DataTable } from '@ooh/ui/components/data-table';
import { StatCard } from '@ooh/ui/components/stat-card';
import { InfoHint } from '@ooh/ui/components/info-hint';
import type { TFunction } from 'i18next';
import {
  getSaleBreakdownRowClassName,
  useRecordSaleColumns,
  type SaleBreakdownRow,
} from './record-sale-columns';

function SkippedStatLabel({ t, count }: { t: TFunction<'offers'>; count: number }) {
  if (count === 0) return <>{t('workspace.header.saleDialog.skippedStat')}</>;
  return (
    <span className="flex items-center gap-1">
      {t('workspace.header.saleDialog.skippedStat')}
      <InfoHint label={t('workspace.header.saleDialog.skippedHintLabel')}>
        {t('workspace.header.saleDialog.skippedHint', { count })}
      </InfoHint>
    </span>
  );
}

export interface SaleBreakdownBodyProps {
  t: TFunction<'offers'>;
  closingCount: number;
  skippedCount: number;
  notCurrentlyAcceptedCount: number;
  includeNotCurrentlyAccepted: boolean;
  onIncludeChange: (checked: boolean) => void;
  columns: ReturnType<typeof useRecordSaleColumns>;
  rows: SaleBreakdownRow[];
}

export function SaleBreakdownBody({
  t,
  closingCount,
  skippedCount,
  notCurrentlyAcceptedCount,
  includeNotCurrentlyAccepted,
  onIncludeChange,
  columns,
  rows,
}: SaleBreakdownBodyProps) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          icon={CheckCircle2}
          tone="success"
          label={t('workspace.header.saleDialog.closedStat')}
          value={closingCount}
          loading={false}
          testId="sale-dialog-stat-closed"
        />
        <StatCard
          icon={AlertTriangle}
          tone="warning"
          label={<SkippedStatLabel t={t} count={notCurrentlyAcceptedCount} />}
          value={skippedCount}
          loading={false}
          testId="sale-dialog-stat-skipped"
        />
      </div>

      {notCurrentlyAcceptedCount > 0 ? (
        <div className="flex items-start gap-2.5 rounded-lg border border-warning/30 bg-warning/10 px-3.5 py-3">
          <Checkbox
            id="record-sale-include-not-accepted"
            checked={includeNotCurrentlyAccepted}
            onCheckedChange={(checked) => onIncludeChange(checked === true)}
          />
          <Label
            htmlFor="record-sale-include-not-accepted"
            className="text-sm font-normal leading-relaxed text-warning"
          >
            {t('workspace.header.saleDialog.includeNotAccepted', {
              count: notCurrentlyAcceptedCount,
            })}
          </Label>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        data={rows}
        getRowKey={(row) => String(row.id)}
        getRowTestId={(row) => `sale-dialog-row-${row.id}`}
        getRowClassName={(row) => getSaleBreakdownRowClassName(row, includeNotCurrentlyAccepted)}
        className="rounded-xl"
      />
    </>
  );
}
